"use client";

import { useEffect, useRef, useState } from "react";
import { backgroundEmbedUrl } from "../parser/video";
import type { PbVideoBackground as VideoSettings } from "../types";

type Props = {
  video: VideoSettings;
};

/**
 * Video background for rows, banners and slides (Magento: jarallax-video).
 *
 * - YouTube / Vimeo iframes (chrome-less, muted, looping) or self-hosted
 *   `mp4:/webm:/ogv:` sources through `<video>`,
 * - `data-video-fallback-src` shown as a poster until playback starts (and
 *   permanently under `prefers-reduced-motion`),
 * - `data-video-lazy-load`: the player is created only near the viewport,
 * - `data-video-play-only-visible`: playback pauses when scrolled out (also
 *   inside a slider, where off-screen slides are clipped),
 * - the iframe is sized to "cover" the container like `background-size: cover`.
 */
export function PbVideoBackground({ video }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(!video.lazyLoad);
  const [playing, setPlaying] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  const source = video.source;

  useEffect(() => {
    setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  // Lazy creation + play/pause on visibility.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible) setMounted(true);
        if (!video.playOnlyVisible) return;
        command(visible ? "play" : "pause");
      },
      { rootMargin: "200px 0px", threshold: 0 }
    );
    io.observe(root);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.playOnlyVisible]);

  // "cover" sizing for 16:9 iframes.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || source?.provider === "file") return;
    const ro = new ResizeObserver(() => {
      const { width, height } = root.getBoundingClientRect();
      if (!width || !height) return;
      const ratio = 16 / 9;
      setBox(width / height > ratio ? { width, height: width / ratio } : { width: height * ratio, height });
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, [source?.provider]);

  function command(action: "play" | "pause") {
    const el = videoRef.current;
    if (el) {
      if (action === "play") void el.play().catch(() => undefined);
      else el.pause();
      return;
    }
    const frame = iframeRef.current?.contentWindow;
    if (!frame || !source) return;
    if (source.provider === "youtube") {
      frame.postMessage(JSON.stringify({ event: "command", func: action === "play" ? "playVideo" : "pauseVideo", args: "" }), "*");
    } else if (source.provider === "vimeo") {
      frame.postMessage(JSON.stringify({ method: action }), "*");
    }
  }

  const poster = video.fallbackImage ? { backgroundImage: `url("${video.fallbackImage.replace(/"/g, "%22")}")` } : undefined;
  const showPlayer = mounted && !!source && !reducedMotion;

  return (
    <div ref={rootRef} className="pb-video-bg" aria-hidden="true">
      {poster && <div className="pb-video-bg__poster" style={poster} data-hidden={playing ? "" : undefined} />}
      {showPlayer && source.provider === "file" && (
        <video
          ref={videoRef}
          className="pb-video-bg__player"
          autoPlay
          muted
          loop={video.loop}
          playsInline
          preload="auto"
          onPlaying={() => setPlaying(true)}
          data-visible={playing ? "" : undefined}
        >
          {source.sources.map((s) => (
            <source key={s.url} src={s.url} type={s.type} />
          ))}
        </video>
      )}
      {showPlayer && source.provider !== "file" && (
        <iframe
          ref={iframeRef}
          className="pb-video-bg__player"
          src={backgroundEmbedUrl(source, video.loop) ?? undefined}
          title=""
          tabIndex={-1}
          allow="autoplay; encrypted-media; picture-in-picture"
          onLoad={() => window.setTimeout(() => setPlaying(true), 600)}
          data-visible={playing ? "" : undefined}
          style={box ? { width: `${box.width}px`, height: `${box.height}px` } : undefined}
        />
      )}
    </div>
  );
}
